import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import CompanyAdminSidebar from '@/components/CompanyAdminSidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Clock, 
  CalendarIcon, 
  Users,
  FileEdit,
  LogOut as LogOutIcon,
  CheckCircle,
  XCircle,
  AlertCircle,
  Edit,
  UserX,
  Trash2,
  Plus,
  Settings
} from 'lucide-react';
import { format, subDays } from 'date-fns';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ManagerControls = () => {
  const [corrections, setCorrections] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [liveStatus, setLiveStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  
  // Modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [showClockOutModal, setShowClockOutModal] = useState(false);
  const [showProcessModal, setShowProcessModal] = useState(false);
  
  // Selected items
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedCorrection, setSelectedCorrection] = useState(null);
  const [selectedEmployeeForClockOut, setSelectedEmployeeForClockOut] = useState(null);
  
  // Form states
  const [editForm, setEditForm] = useState({ 
    clock_in: '', 
    clock_out: '',
    breakfast_start: '',
    breakfast_end: '',
    lunch_start: '',
    lunch_end: ''
  });
  const [processForm, setProcessForm] = useState({ status: 'approved', admin_notes: '' });
  const [clockOutTime, setClockOutTime] = useState('');
  
  const [submitting, setSubmitting] = useState(false);
  const [deleteEntry, setDeleteEntry] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    employee_id: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    clock_in_time: '',
    clock_out_time: '',
    breakfast_start: '',
    breakfast_end: '',
    lunch_start: '',
    lunch_end: '',
    notes: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const params = { date: dateStr };
      if (selectedEmployee !== 'all') {
        params.employee_id = selectedEmployee;
      }
      
      const [correctionsRes, entriesRes, employeesRes, statusRes] = await Promise.all([
        axios.get(`${API}/shift-corrections`),
        axios.get(`${API}/admin/time-entries`, { params }),
        axios.get(`${API}/employees`),
        axios.get(`${API}/status/live`)
      ]);
      
      setCorrections(correctionsRes.data || []);
      setTimeEntries(entriesRes.data.entries || []);
      setEmployees(employeesRes.data || []);
      setLiveStatus(statusRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, selectedEmployee]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get clocked-in employees for force clock-out
  const clockedInEmployees = liveStatus.filter(e => e.status === 'clocked_in' || e.status === 'on_break');
  
  // Pending corrections count
  const pendingCorrections = corrections.filter(c => c.status === 'pending');

  const openEditModal = (entry) => {
    // Format UTC times to local datetime-local input values
    const toLocalInput = (isoStr) => {
      if (!isoStr) return '';
      const d = new Date(isoStr);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    
    const clockIn = toLocalInput(entry.clock_in);
    const clockOut = toLocalInput(entry.clock_out);
    
    // Extract break times from entry.breaks array
    const breakfastBreak = entry.breaks?.find(b => b.type === 'breakfast');
    const lunchBreak = entry.breaks?.find(b => b.type === 'lunch');
    
    const toLocalTime = (isoStr) => {
      if (!isoStr) return '';
      const d = new Date(isoStr);
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };
    
    const breakfastStart = toLocalTime(breakfastBreak?.start_time);
    const breakfastEnd = toLocalTime(breakfastBreak?.end_time);
    const lunchStart = toLocalTime(lunchBreak?.start_time);
    const lunchEnd = toLocalTime(lunchBreak?.end_time);
    
    setEditForm({ 
      clock_in: clockIn, 
      clock_out: clockOut,
      breakfast_start: breakfastStart,
      breakfast_end: breakfastEnd,
      lunch_start: lunchStart,
      lunch_end: lunchEnd
    });
    setSelectedEntry(entry);
    setShowEditModal(true);
  };

  const handleEditEntry = async () => {
    if (!selectedEntry) return;
    
    setSubmitting(true);
    try {
      await axios.put(`${API}/admin/time-entries/${selectedEntry.id}`, {
        entry_id: selectedEntry.id,
        clock_in: editForm.clock_in ? new Date(editForm.clock_in).toISOString() : null,
        clock_out: editForm.clock_out ? new Date(editForm.clock_out).toISOString() : null,
        breakfast_start: editForm.breakfast_start || null,
        breakfast_end: editForm.breakfast_end || null,
        lunch_start: editForm.lunch_start || null,
        lunch_end: editForm.lunch_end || null
      });
      toast.success('Time entry updated!');
      setShowEditModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEntry = async () => {
    if (!deleteEntry) return;
    
    setSubmitting(true);
    try {
      await axios.delete(`${API}/admin/time-entries/${deleteEntry.id}`);
      toast.success('Time entry deleted');
      setDeleteEntry(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateEntry = async () => {
    if (!createForm.employee_id || !createForm.date || !createForm.clock_in_time) {
      toast.error('Employee, date, and clock-in time are required');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/time-entries/create`, {
        employee_id: createForm.employee_id,
        date: createForm.date,
        clock_in_time: createForm.clock_in_time,
        clock_out_time: createForm.clock_out_time || null,
        breakfast_start: createForm.breakfast_start || null,
        breakfast_end: createForm.breakfast_end || null,
        lunch_start: createForm.lunch_start || null,
        lunch_end: createForm.lunch_end || null,
        notes: createForm.notes || null
      });
      toast.success('Time entry created');
      setShowCreateModal(false);
      setCreateForm({ employee_id: '', date: format(new Date(), 'yyyy-MM-dd'), clock_in_time: '', clock_out_time: '', breakfast_start: '', breakfast_end: '', lunch_start: '', lunch_end: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create entry');
    } finally {
      setSubmitting(false);
    }
  };

  const openClockOutModal = (employee) => {
    setSelectedEmployeeForClockOut(employee);
    setClockOutTime('');
    setShowClockOutModal(true);
  };

  const handleForceClockOut = async () => {
    if (!selectedEmployeeForClockOut) return;
    
    setSubmitting(true);
    try {
      const payload = {
        employee_id: selectedEmployeeForClockOut.id,
        clock_out_time: clockOutTime ? new Date(clockOutTime).toISOString() : null
      };
      
      await axios.post(`${API}/admin/clock-out-employee`, payload);
      toast.success(`${selectedEmployeeForClockOut.name} has been clocked out!`);
      setShowClockOutModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock out employee');
    } finally {
      setSubmitting(false);
    }
  };

  const openProcessModal = (correction) => {
    setSelectedCorrection(correction);
    setProcessForm({ status: 'approved', admin_notes: '' });
    setShowProcessModal(true);
  };

  const handleProcessCorrection = async () => {
    if (!selectedCorrection) return;
    
    setSubmitting(true);
    try {
      await axios.put(`${API}/shift-corrections/${selectedCorrection.id}`, processForm);
      toast.success(`Correction ${processForm.status}!`);
      setShowProcessModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process correction');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-[hsl(var(--success))] text-white">Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Rejected</Badge>;
      default:
        return <Badge variant="secondary">Pending</Badge>;
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-slate-50 w-full overflow-x-hidden">
      <CompanyAdminSidebar />
      
      <main className="flex-1 min-w-0 p-3 sm:p-4 md:p-8 pb-20 md:pb-8 overflow-x-hidden w-full">
        <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6 w-full">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold font-['Manrope'] tracking-tight">Manager Controls</h1>
              <p className="text-sm sm:text-base text-slate-500">Manage time entries, corrections, and clock-outs</p>
            </div>
            {pendingCorrections.length > 0 && (
              <Badge className="bg-[hsl(var(--warning))] text-white text-sm px-3 py-1 self-start sm:self-auto">
                {pendingCorrections.length} pending
              </Badge>
            )}
          </div>

          <Tabs defaultValue="corrections" className="space-y-4 sm:space-y-6">
            <TabsList className="grid w-full grid-cols-3 h-auto p-1">
              <TabsTrigger value="corrections" className="flex-col sm:flex-row gap-1 sm:gap-2 py-2 px-2 sm:px-4 text-xs sm:text-sm" data-testid="tab-corrections">
                <FileEdit className="w-4 h-4" />
                <span>Corrections</span>
                {pendingCorrections.length > 0 && (
                  <span className="bg-[hsl(var(--warning))] text-white text-xs rounded-full px-1.5 ml-0 sm:ml-1">
                    {pendingCorrections.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="entries" className="flex-col sm:flex-row gap-1 sm:gap-2 py-2 px-2 sm:px-4 text-xs sm:text-sm" data-testid="tab-entries">
                <Clock className="w-4 h-4" />
                <span>Entries</span>
              </TabsTrigger>
              <TabsTrigger value="clockout" className="flex-col sm:flex-row gap-1 sm:gap-2 py-2 px-2 sm:px-4 text-xs sm:text-sm" data-testid="tab-clockout">
                <UserX className="w-4 h-4" />
                <span>Clock-Out</span>
              </TabsTrigger>
            </TabsList>

            {/* Correction Requests Tab */}
            <TabsContent value="corrections">
              <Card className="border-0 shadow-lg">
                <CardHeader className="border-b p-4 sm:p-6">
                  <CardTitle className="font-['Manrope'] flex items-center gap-2 text-base sm:text-lg">
                    <FileEdit className="w-5 h-5" />
                    Shift Correction Requests
                  </CardTitle>
                  <CardDescription className="text-sm">Review and process employee correction requests</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  {loading ? (
                    <div className="flex justify-center py-12">
                      <div className="spinner" />
                    </div>
                  ) : corrections.length === 0 ? (
                    <div className="empty-state py-12">
                      <FileEdit className="empty-state-icon" />
                      <p className="text-slate-500">No correction requests</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-[calc(100vh-350px)] min-h-[300px] max-h-[500px]">
                      <div className="divide-y">
                        {corrections.map((correction) => (
                          <div 
                            key={correction.id} 
                            className="p-3 sm:p-4 hover:bg-slate-50 transition-colors"
                            data-testid={`correction-item-${correction.id}`}
                          >
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                              <div className="space-y-2 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-medium text-sm sm:text-base">{correction.user_name}</p>
                                  <span className="text-slate-400">•</span>
                                  <p className="text-slate-600 text-sm">{correction.date}</p>
                                  {getStatusBadge(correction.status)}
                                </div>
                                <p className="text-sm text-slate-600 bg-slate-100 p-2 rounded">
                                  <span className="font-medium">Reason:</span> {correction.reason}
                                </p>
                                <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                                  {correction.requested_clock_in && (
                                    <div>
                                      <p className="text-slate-500">Clock In</p>
                                      <p className="font-mono">{correction.requested_clock_in}</p>
                                    </div>
                                  )}
                                  {correction.requested_clock_out && (
                                    <div>
                                      <p className="text-slate-500">Clock Out</p>
                                      <p className="font-mono">{correction.requested_clock_out}</p>
                                    </div>
                                  )}
                                  {correction.requested_breakfast_start && (
                                    <div>
                                      <p className="text-slate-500">Breakfast</p>
                                      <p className="font-mono">{correction.requested_breakfast_start} - {correction.requested_breakfast_end || '?'}</p>
                                    </div>
                                  )}
                                  {correction.requested_lunch_start && (
                                    <div>
                                      <p className="text-slate-500">Lunch</p>
                                      <p className="font-mono">{correction.requested_lunch_start} - {correction.requested_lunch_end || '?'}</p>
                                    </div>
                                  )}
                                </div>
                                {correction.admin_notes && (
                                  <p className="text-xs sm:text-sm text-slate-600 italic">
                                    Admin notes: {correction.admin_notes}
                                  </p>
                                )}
                                <p className="text-xs text-slate-400">
                                  Submitted: {format(new Date(correction.created_at), 'dd/MM/yyyy')}
                                </p>
                              </div>
                              {correction.status === 'pending' && (
                                <div className="flex gap-2 self-end sm:self-start">
                                  <Button
                                    size="sm"
                                    className="bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/90 text-xs sm:text-sm"
                                    onClick={() => {
                                      setSelectedCorrection(correction);
                                      setProcessForm({ status: 'approved', admin_notes: '' });
                                      setShowProcessModal(true);
                                    }}
                                    data-testid={`approve-correction-${correction.id}`}
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => {
                                      setSelectedCorrection(correction);
                                      setProcessForm({ status: 'rejected', admin_notes: '' });
                                      setShowProcessModal(true);
                                    }}
                                    data-testid={`reject-correction-${correction.id}`}
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Time Entries Tab */}
            <TabsContent value="entries">
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="font-['Manrope'] flex items-center gap-2">
                        <Clock className="w-5 h-5" />
                        Time Entries
                      </CardTitle>
                      <CardDescription>View, edit, and create employee time entries</CardDescription>
                    </div>
                    <Button
                      onClick={() => {
                        setCreateForm({ employee_id: '', date: format(selectedDate, 'yyyy-MM-dd'), clock_in_time: '', clock_out_time: '', breakfast_start: '', breakfast_end: '', lunch_start: '', lunch_end: '', notes: '' });
                        setShowCreateModal(true);
                      }}
                      data-testid="create-entry-btn"
                      size="sm"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Create Entry
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Filters */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !selectedDate && "text-muted-foreground"
                            )}
                            data-testid="entries-date-picker"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {selectedDate ? format(selectedDate, "dd/MM/yyyy") : "Pick a date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={selectedDate}
                            onSelect={(date) => setSelectedDate(date || new Date())}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label>Employee</Label>
                      <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                        <SelectTrigger data-testid="entries-employee-select">
                          <SelectValue placeholder="Select employee" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4" />
                              All Employees
                            </div>
                          </SelectItem>
                          {employees.map((emp) => (
                            <SelectItem key={emp.id} value={emp.id}>
                              {emp.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Entries List */}
                  {loading ? (
                    <div className="flex justify-center py-12">
                      <div className="spinner" />
                    </div>
                  ) : timeEntries.length === 0 ? (
                    <div className="empty-state py-12">
                      <Clock className="empty-state-icon" />
                      <p className="text-slate-500">No time entries for this date</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-[400px]">
                      <div className="space-y-2">
                        {timeEntries.map((entry) => (
                          <div 
                            key={entry.id} 
                            className="p-3 sm:p-4 bg-slate-50 rounded-lg"
                            data-testid={`time-entry-${entry.id}`}
                          >
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                              <div className="space-y-2 min-w-0 flex-1">
                                <p className="font-medium truncate">{entry.user_name}</p>
                                <div className="flex items-center gap-2 sm:gap-4 text-sm flex-wrap">
                                  <div>
                                    <span className="text-slate-500">In: </span>
                                    <span className="font-mono text-xs sm:text-sm">{formatTime(entry.clock_in)}</span>
                                  </div>
                                  <div>
                                    <span className="text-slate-500">Out: </span>
                                    <span className="font-mono text-xs sm:text-sm">{entry.clock_out ? formatTime(entry.clock_out) : '-'}</span>
                                  </div>
                                  {entry.total_hours && (
                                    <div>
                                      <span className="text-slate-500">Total: </span>
                                      <span className="font-mono text-xs sm:text-sm text-[hsl(var(--success))]">{entry.total_hours.toFixed(2)}h</span>
                                    </div>
                                  )}
                                </div>
                                {entry.office_name && (
                                  <Badge variant="outline" className="text-xs">{entry.office_name}</Badge>
                                )}
                                {/* Break information */}
                                {entry.breaks && entry.breaks.length > 0 && (
                                  <div className="flex flex-wrap gap-2 mt-1">
                                    {entry.breaks.map((brk, idx) => (
                                      <div 
                                        key={idx} 
                                        className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                                          brk.type === 'breakfast' 
                                            ? 'bg-amber-100 text-amber-700' 
                                            : 'bg-emerald-100 text-emerald-700'
                                        }`}
                                      >
                                        <span className="capitalize">{brk.type}:</span>
                                        <span className="font-mono">
                                          {formatTime(brk.start_time)} - {brk.end_time ? formatTime(brk.end_time) : 'ongoing'}
                                        </span>
                                        {brk.duration_minutes && (
                                          <span className="opacity-70">({Math.round(brk.duration_minutes)}m)</span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="flex gap-2 self-end sm:self-start shrink-0">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openEditModal(entry)}
                                  data-testid={`edit-entry-${entry.id}`}
                                >
                                  <Edit className="w-4 h-4 sm:mr-1" />
                                  <span className="hidden sm:inline">Edit</span>
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setDeleteEntry(entry)}
                                  data-testid={`delete-entry-${entry.id}`}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                                >
                                  <Trash2 className="w-4 h-4 sm:mr-1" />
                                  <span className="hidden sm:inline">Delete</span>
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Force Clock-Out Tab */}
            <TabsContent value="clockout">
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="font-['Manrope'] flex items-center gap-2">
                    <UserX className="w-5 h-5" />
                    Force Clock-Out
                  </CardTitle>
                  <CardDescription>Clock out employees who forgot to clock out</CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex justify-center py-12">
                      <div className="spinner" />
                    </div>
                  ) : clockedInEmployees.length === 0 ? (
                    <div className="empty-state py-12">
                      <CheckCircle className="empty-state-icon text-[hsl(var(--success))]" />
                      <p className="text-slate-500">All employees are clocked out</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {clockedInEmployees.map((employee) => (
                        <div 
                          key={employee.id} 
                          className="p-3 sm:p-4 bg-slate-50 rounded-lg flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between"
                          data-testid={`clocked-in-employee-${employee.id}`}
                        >
                          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                            <div className="relative shrink-0">
                              <div className="employee-avatar">
                                {employee.name.charAt(0).toUpperCase()}
                              </div>
                              <div className={`status-dot absolute -bottom-0.5 -right-0.5 ${employee.status === 'on_break' ? 'break' : 'active'}`} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium truncate">{employee.name}</p>
                              <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-500 flex-wrap">
                                <span>
                                  {employee.status === 'on_break' 
                                    ? `On ${employee.break_type} break`
                                    : 'Clocked in'}
                                </span>
                                {employee.clock_in_time && (
                                  <>
                                    <span className="hidden sm:inline">•</span>
                                    <span className="font-mono">{formatTime(employee.clock_in_time)}</span>
                                  </>
                                )}
                                {employee.office_name && (
                                  <>
                                    <span className="hidden sm:inline">•</span>
                                    <span className="truncate max-w-[100px] sm:max-w-none">{employee.office_name}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => openClockOutModal(employee)}
                            data-testid={`force-clockout-${employee.id}`}
                            className="self-end sm:self-auto shrink-0"
                          >
                            <LogOutIcon className="w-4 h-4 sm:mr-1" />
                            <span className="hidden sm:inline">Clock Out</span>
                            <span className="sm:hidden">Clock Out</span>
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Edit Time Entry Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Edit Time Entry</DialogTitle>
            <DialogDescription>
              Modify times for {selectedEntry?.user_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Clock In/Out Times */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Clock In</Label>
                <Input
                  type="datetime-local"
                  value={editForm.clock_in}
                  onChange={(e) => setEditForm(prev => ({ ...prev, clock_in: e.target.value }))}
                  data-testid="edit-clock-in"
                />
              </div>
              <div className="space-y-2">
                <Label>Clock Out</Label>
                <Input
                  type="datetime-local"
                  value={editForm.clock_out}
                  onChange={(e) => setEditForm(prev => ({ ...prev, clock_out: e.target.value }))}
                  data-testid="edit-clock-out"
                />
              </div>
            </div>

            {/* Breakfast Break */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                Breakfast Break
              </Label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-slate-500">Start</Label>
                  <Input
                    type="time"
                    value={editForm.breakfast_start}
                    onChange={(e) => setEditForm(prev => ({ ...prev, breakfast_start: e.target.value }))}
                    data-testid="edit-breakfast-start"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">End</Label>
                  <Input
                    type="time"
                    value={editForm.breakfast_end}
                    onChange={(e) => setEditForm(prev => ({ ...prev, breakfast_end: e.target.value }))}
                    data-testid="edit-breakfast-end"
                  />
                </div>
              </div>
            </div>

            {/* Lunch Break */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                Lunch Break
              </Label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-slate-500">Start</Label>
                  <Input
                    type="time"
                    value={editForm.lunch_start}
                    onChange={(e) => setEditForm(prev => ({ ...prev, lunch_start: e.target.value }))}
                    data-testid="edit-lunch-start"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">End</Label>
                  <Input
                    type="time"
                    value={editForm.lunch_end}
                    onChange={(e) => setEditForm(prev => ({ ...prev, lunch_end: e.target.value }))}
                    data-testid="edit-lunch-end"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleEditEntry}
              disabled={submitting}
              className="bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)]"
              data-testid="save-edit-btn"
            >
              {submitting ? <div className="spinner" /> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Force Clock-Out Modal */}
      <Dialog open={showClockOutModal} onOpenChange={setShowClockOutModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Force Clock-Out</DialogTitle>
            <DialogDescription>
              Clock out {selectedEmployeeForClockOut?.name}. Optionally specify a custom time.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-slate-100 rounded-lg">
              <p className="text-sm text-slate-600">
                Leave empty to use current time, or specify when they should have clocked out.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Clock Out Time (optional)</Label>
              <Input
                type="datetime-local"
                value={clockOutTime}
                onChange={(e) => setClockOutTime(e.target.value)}
                data-testid="force-clockout-time"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClockOutModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleForceClockOut}
              disabled={submitting}
              data-testid="confirm-force-clockout-btn"
            >
              {submitting ? <div className="spinner" /> : 'Clock Out Employee'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Process Correction Modal */}
      <Dialog open={showProcessModal} onOpenChange={setShowProcessModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {processForm.status === 'approved' ? 'Approve' : 'Reject'} Correction
            </DialogTitle>
            <DialogDescription>
              {processForm.status === 'approved' 
                ? 'This will apply the requested changes to the time entry.'
                : 'This will reject the correction request.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {selectedCorrection && (
              <div className="p-4 bg-slate-100 rounded-lg space-y-2">
                <p className="font-medium">{selectedCorrection.user_name} - {selectedCorrection.date}</p>
                <p className="text-sm text-slate-600">{selectedCorrection.reason}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Admin Notes (optional)</Label>
              <Textarea
                placeholder="Add any notes about this decision..."
                value={processForm.admin_notes}
                onChange={(e) => setProcessForm(prev => ({ ...prev, admin_notes: e.target.value }))}
                data-testid="process-admin-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProcessModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleProcessCorrection}
              disabled={submitting}
              className={processForm.status === 'approved' 
                ? 'bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/90'
                : ''}
              variant={processForm.status === 'rejected' ? 'destructive' : 'default'}
              data-testid="confirm-process-btn"
            >
              {submitting ? <div className="spinner" /> : (processForm.status === 'approved' ? 'Approve' : 'Reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteEntry} onOpenChange={(open) => !open && setDeleteEntry(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-red-600">Delete Time Entry</DialogTitle>
            <DialogDescription>
              This action cannot be undone. The time entry and associated breaks will be permanently removed.
            </DialogDescription>
          </DialogHeader>
          {deleteEntry && (
            <div className="py-3 space-y-2">
              <p className="text-sm"><span className="font-medium">Employee:</span> {deleteEntry.user_name}</p>
              <p className="text-sm"><span className="font-medium">Clock In:</span> {formatTime(deleteEntry.clock_in)}</p>
              <p className="text-sm"><span className="font-medium">Clock Out:</span> {deleteEntry.clock_out ? formatTime(deleteEntry.clock_out) : '-'}</p>
              {deleteEntry.total_hours != null && (
                <p className="text-sm"><span className="font-medium">Total Hours:</span> {deleteEntry.total_hours.toFixed(2)}h</p>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteEntry(null)} data-testid="cancel-delete-btn">
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteEntry} 
              disabled={submitting}
              data-testid="confirm-delete-btn"
            >
              {submitting ? 'Deleting...' : 'Delete Entry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Time Entry Dialog */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Create Time Entry</DialogTitle>
            <DialogDescription>
              Manually create a time entry for an employee who forgot to clock in.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <Select value={createForm.employee_id} onValueChange={(v) => setCreateForm(f => ({ ...f, employee_id: v }))}>
                <SelectTrigger data-testid="create-employee-select">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={createForm.date}
                onChange={(e) => setCreateForm(f => ({ ...f, date: e.target.value }))}
                data-testid="create-date-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Clock In *</Label>
                <Input
                  type="time"
                  value={createForm.clock_in_time}
                  onChange={(e) => setCreateForm(f => ({ ...f, clock_in_time: e.target.value }))}
                  data-testid="create-clock-in-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Clock Out</Label>
                <Input
                  type="time"
                  value={createForm.clock_out_time}
                  onChange={(e) => setCreateForm(f => ({ ...f, clock_out_time: e.target.value }))}
                  data-testid="create-clock-out-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Breakfast Start</Label>
                <Input
                  type="time"
                  value={createForm.breakfast_start}
                  onChange={(e) => setCreateForm(f => ({ ...f, breakfast_start: e.target.value }))}
                  data-testid="create-breakfast-start"
                />
              </div>
              <div className="space-y-2">
                <Label>Breakfast End</Label>
                <Input
                  type="time"
                  value={createForm.breakfast_end}
                  onChange={(e) => setCreateForm(f => ({ ...f, breakfast_end: e.target.value }))}
                  data-testid="create-breakfast-end"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Lunch Start</Label>
                <Input
                  type="time"
                  value={createForm.lunch_start}
                  onChange={(e) => setCreateForm(f => ({ ...f, lunch_start: e.target.value }))}
                  data-testid="create-lunch-start"
                />
              </div>
              <div className="space-y-2">
                <Label>Lunch End</Label>
                <Input
                  type="time"
                  value={createForm.lunch_end}
                  onChange={(e) => setCreateForm(f => ({ ...f, lunch_end: e.target.value }))}
                  data-testid="create-lunch-end"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                placeholder="e.g. Forgot to clock in"
                value={createForm.notes}
                onChange={(e) => setCreateForm(f => ({ ...f, notes: e.target.value }))}
                data-testid="create-notes-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowCreateModal(false)} data-testid="cancel-create-btn">
              Cancel
            </Button>
            <Button
              onClick={handleCreateEntry}
              disabled={submitting || !createForm.employee_id || !createForm.clock_in_time}
              data-testid="confirm-create-btn"
            >
              {submitting ? 'Creating...' : 'Create Entry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManagerControls;
